import * as React from 'react'
import type { UserReducer } from 'types/user'

import memoize from 'memoize-one'
import ChannelSelector from 'components/common/channel-selector'
import { CUSTOM_CHANNEL, TERRITORY } from '@disco/constants/GroupTypes'
import type { Shortlist } from 'types/shortlist'
import type { Group, GroupWithNesting } from 'types/group'

import { getEnabledFolders } from '@disco/utils/groups'

type Props = {
  defaultGroup?: Group
  myLibraryGroup?: Group
  groupsWithNesting: GroupWithNesting[]
  user: UserReducer
  shortlistId: string
  shortlist: Shortlist
  allGroups: Group[]
  sendMixpanelEvent: (event: string) => void
  updateShortlist: (
    shortlistId: string,
    updatedFields: Record<string, any>,
  ) => void
}

// Memoized function that filters and maps the groups to selectedGroups
const getSelectedGroups = memoize((groups, defaultGroup, myLibraryGroup) => {
  const activeGroups = groups.filter(g => {
    if (!g) return false
    if (g.group_type === TERRITORY) return false
    if (defaultGroup && defaultGroup.id === g.id) return false
    if (myLibraryGroup && myLibraryGroup.id === g.id) return false
    return true
  })
  return activeGroups.map(g => ({
    label: g.name,
    value: g,
    icon: g.parent ? 'folder-outline' : 'folder',
  }))
})

const isInActiveGroups = (group: Group, shortlistGroups: Group[]) => {
  return shortlistGroups.some(activeGroup => {
    return activeGroup && activeGroup.id === group.id
  })
}

export const ShortlistGroupsInput = ({
  defaultGroup,
  myLibraryGroup,
  groupsWithNesting,
  user,
  shortlist,
  allGroups,
  sendMixpanelEvent,
  updateShortlist,
  shortlistId,
}: Props) => {
  const channels: GroupWithNesting[] = React.useMemo(
    () =>
      groupsWithNesting.filter(group => {
        return (
          (group.group_type === CUSTOM_CHANNEL &&
            !group.parent &&
            group.permissions.content) ||
          (group.group_type === TERRITORY &&
            user.territories.includes(group.id))
        )
      }),
    [groupsWithNesting, user],
  )

  const updateGroupToShortlist = React.useCallback(
    groups => {
      // backend sometimes does not include team librarary in group list
      const allGroupsWithDefault = allGroups
        .concat(myLibraryGroup ? [myLibraryGroup] : [])
        .concat(defaultGroup ? [defaultGroup] : [])
      const newGroup = groups.map(group => {
        return allGroupsWithDefault.find(
          g => g.id === group.value || g.id === group.value.id,
        )
      })

      const defaultGroups = shortlist.groups.filter(
        g =>
          g.group_type === defaultGroup?.group_type ||
          g.id === myLibraryGroup?.id,
      )

      if (newGroup) {
        const newGroups = [...newGroup, ...defaultGroups]
        sendMixpanelEvent('Playlist Info tab - Playlist added to channel')
        updateShortlist(shortlistId, {
          groups: newGroups,
        })
      }
    },
    [
      allGroups,
      myLibraryGroup,
      defaultGroup,
      shortlist.groups,
      sendMixpanelEvent,
      updateShortlist,
      shortlistId,
    ],
  )

  const groups = React.useMemo(
    () =>
      getEnabledFolders(
        allGroups,
        channels,
        g => !isInActiveGroups(g, shortlist.groups),
      ),
    [allGroups, channels, shortlist.groups],
  )

  const selectedGroups = React.useMemo(
    () => getSelectedGroups(shortlist.groups, defaultGroup, myLibraryGroup),
    [shortlist.groups, defaultGroup, myLibraryGroup],
  )

  return (
    <ChannelSelector
      placeholder="Search channel"
      channels={groups as Group[]}
      selectedChannels={selectedGroups}
      onChannelSelect={updateGroupToShortlist}
      enableUp
    />
  )
}
